import { useEffect, useRef, useState } from 'react'
import type { BuildItem, BuildResponse } from '@/components/build/types'
import type { Recipe } from '@/lib/buildLink'

const DEBOUNCE_MS = 220

// A small, read-only taste of the playlist builder: the same endpoint, a
// fixed seed (so turning the dial reshuffles smoothly instead of rolling a
// new ride each time), and only the picks -- no locking or swapping.
export function useRideTeaser(instructorId: string, recipe: Recipe | null, adventure: number, seed: number) {
  const [picks, setPicks] = useState<BuildItem[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const requestId = useRef(0)
  const first = useRef(true)

  const discipline = recipe?.discipline
  const minutes = recipe?.minutes
  const theme = recipe?.theme

  useEffect(() => {
    if (!discipline || !minutes) return

    // The first deal is immediate; later ones (dial moves) are debounced.
    const delay = first.current ? 0 : DEBOUNCE_MS
    first.current = false

    const timer = setTimeout(async () => {
      const id = ++requestId.current
      setLoading(true)
      const query = new URLSearchParams({ instructor_id: instructorId, discipline, minutes: String(minutes), adventure: String(adventure), seed: String(seed) })
      if (theme) query.set('theme', theme)

      try {
        const response = await fetch(`/api/playlist_builder/generate?${query}`, { headers: { Accept: 'application/json' } })
        if (!response.ok) throw new Error(`Request failed: ${response.status}`)
        const data = (await response.json()) as BuildResponse
        if (id !== requestId.current) return
        setPicks(data.slots.flatMap((slot) => (slot.pick ? [slot.pick] : [])))
        setError(false)
      } catch {
        if (id === requestId.current) setError(true)
      } finally {
        if (id === requestId.current) setLoading(false)
      }
    }, delay)

    return () => clearTimeout(timer)
  }, [instructorId, discipline, minutes, theme, adventure, seed])

  return { picks, loading, error }
}
